import React, { useMemo } from 'react';
import { useQuestionnaireContext } from '../contexts/questionnaire-context';
import Questions from './questions';
import { FormControl } from 'baseui/form-control';
import { INPUT_TYPE, TextType } from '../constants';
import SelectInput from './inputs/select-input';
import TextareaInput from './inputs/textarea-input';
import { getErrorMessageFromQuestion, isRadio, getVisibleFollowUpQuestionIds } from '../utils';
import RadioInput from './inputs/radio-input';
import FileInput from './inputs/file-input';
const Question = ({
  questionId
}: {
  questionId: string;
}): React.ReactElement | null => {
  const {
    questionsMap,
    questionnaireAnswers,
    overrides,
    setAnswerForQuestion,
    renderText,
    renderQuestionTopContent,
    renderQuestionBottomContent,
    registerQuestionNode,
    processFileOnDrop
  } = useQuestionnaireContext();
  const question = questionsMap.get(questionId);
  const answer = questionnaireAnswers.get(questionId);
  const followUpQuestionIds = useMemo(() => {
    if (!question?.followUpQuestions) return [];
    return getVisibleFollowUpQuestionIds(question.followUpQuestions, questionnaireAnswers, question);
  }, [question, questionnaireAnswers]);
  if (!question) {
    return null;
  }
  const renderInput = () => {
    switch (question?.type) {
      case INPUT_TYPE.SELECT:
        if (isRadio(question.tags)) {
          return <RadioInput question={question} answer={answer} radioGroupOverrides={overrides.RadioGroupOverrides} radioOverrides={overrides.RadioOverrides} setAnswerForQuestion={setAnswerForQuestion} />;
        }
        return <SelectInput question={question} answer={answer} overrides={overrides.SelectOverrides} setAnswerForQuestion={setAnswerForQuestion} renderText={renderText} />;
      case INPUT_TYPE.INPUT:
        return <TextareaInput question={question} answer={answer} overrides={overrides.TextAreaOverrides} setAnswerForQuestion={setAnswerForQuestion} />;
      case INPUT_TYPE.FILE:
        return <FileInput question={question} answer={answer} setAnswerForQuestion={setAnswerForQuestion} processFileOnDrop={processFileOnDrop} />;
      default:
        return <></>;
    }
  };
  const renderQuestionLabel = () => {
    const labelFromConsumer = renderText({
      question,
      textType: TextType.FORMCONTROL_LABEL
    });
    return labelFromConsumer ?? question?.question;
  };
  const renderErrorMessage = (): React.ReactNode | undefined => {
    const errorMessageFromConsumer = renderText({
      question,
      textType: TextType.FORM_CONTROL_ERROR
    });
    return errorMessageFromConsumer ?? getErrorMessageFromQuestion(question);
  };
  return <div ref={el => registerQuestionNode(questionId, el ?? null)}>
      {renderQuestionTopContent({
      question
    })}
      <FormControl label={renderQuestionLabel()} overrides={overrides.FormControlOverrides} error={renderErrorMessage()}>
        {renderInput()}
      </FormControl>
      {renderQuestionBottomContent({
      question
    })}
      {followUpQuestionIds.length > 0 && <Questions questionIds={followUpQuestionIds} />}
    </div>;
};
export default Question;